/**
 * ProxyClient — routes PocketBase write operations through the authenticated proxy server.
 *
 * Mirrors the PocketBase SDK's collection().create/update/delete() interface so
 * migration is mechanical: replace `this.pb.collection(X).create(...)` with
 * `this.proxy.collection(X).create(...)`.
 *
 * All requests include the Clerk JWT for server-side identity verification.
 * The proxy enforces ownership on join tables (user_movies, user_games, etc.)
 * by setting the `user` field from the verified session — never from the client.
 */

function getApiBase() {
    const proxyUrl = typeof import.meta !== 'undefined' && import.meta.env?.VITE_PROXY_URL;
    return proxyUrl ? `${proxyUrl.replace(/\/+$/, '')}/api` : '/api';
}

/**
 * Build an Error from a failed proxy response. Proxy-originated errors are `{ error }`;
 * PocketBase errors are relayed verbatim as `{ message, data: { field: { code, message } } }`,
 * so include the message and per-field reasons instead of only the HTTP status.
 * The parsed body is kept on `error.data` for callers that inspect field codes.
 */
function buildError(res, body, fallback) {
    const detail = body.error || body.message || `${fallback}: ${res.status}`;
    const fieldErrors = body.data && typeof body.data === 'object'
        ? Object.entries(body.data).map(([k, v]) => `${k}: ${v?.message || v}`).join('; ')
        : '';
    const error = new Error(fieldErrors ? `${detail} (${fieldErrors})` : detail);
    error.status = res.status;
    error.data = body;
    return error;
}

class CollectionProxy {
    constructor(collectionName, getToken) {
        this._name = collectionName;
        this._getToken = getToken;
        this._apiBase = getApiBase();
    }

    async _authHeaders(json = true) {
        const h = {};
        if (this._getToken) {
            try {
                const token = await this._getToken();
                if (token) h['Authorization'] = `Bearer ${token}`;
            } catch (_) { /* no token available */ }
        }
        if (json) h['Content-Type'] = 'application/json';
        return h;
    }

    /**
     * Create a new record.
     * @param {Object|FormData} data - Record data (JSON object or FormData for file uploads)
     * @returns {Object} Created record
     */
    async create(data) {
        const isFormData = typeof FormData !== 'undefined' && data instanceof FormData;
        const headers = await this._authHeaders(!isFormData);

        const res = await fetch(`${this._apiBase}/pb/${this._name}`, {
            method: 'POST',
            headers,
            body: isFormData ? data : JSON.stringify(data),
        });

        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw buildError(res, err, 'Create failed');
        }
        return res.json();
    }

    /**
     * Update an existing record.
     * @param {string} id - Record ID
     * @param {Object|FormData} data - Fields to update
     * @returns {Object} Updated record
     */
    async update(id, data) {
        const isFormData = typeof FormData !== 'undefined' && data instanceof FormData;
        const headers = await this._authHeaders(!isFormData);

        const res = await fetch(`${this._apiBase}/pb/${this._name}/${id}`, {
            method: 'PATCH',
            headers,
            body: isFormData ? data : JSON.stringify(data),
        });

        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw buildError(res, err, 'Update failed');
        }
        return res.json();
    }

    /**
     * Delete a record.
     * @param {string} id - Record ID
     */
    async delete(id) {
        const headers = await this._authHeaders(false);

        const res = await fetch(`${this._apiBase}/pb/${this._name}/${id}`, {
            method: 'DELETE',
            headers,
        });

        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw buildError(res, err, 'Delete failed');
        }
        return true;
    }

    /**
     * Always create a new watch record (never find-and-update).
     * Use this for rewatches to preserve full history.
     * @param {Object} filter - Relation fields (e.g. { movie: "abc123" })
     * @param {Object} data - Fields to set (watchedDate, firstTime, etc.)
     * @param {string} [targetUser] - Optional: write as another pod member
     * @returns {Object} Newly created record
     */
    async createWatch(filter, data, targetUser) {
        const headers = await this._authHeaders(true);
        const body = { filter, data };
        if (targetUser) body.targetUser = targetUser;
        const res = await fetch(`${this._apiBase}/pb/${this._name}/create-watch`, {
            method: 'POST',
            headers,
            body: JSON.stringify(body),
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            // Include PocketBase's detailed validation message if available
            const detail = err.message || err.error || `Create watch failed: ${res.status}`;
            const fieldErrors = err.data ? Object.entries(err.data).map(([k, v]) => `${k}: ${v?.message || v}`).join('; ') : '';
            const error = new Error(fieldErrors ? `${detail} (${fieldErrors})` : detail);
            error.status = res.status;
            error.data = err;
            throw error;
        }
        return res.json();
    }

    /**
     * Upsert a record on a join table. The server injects the authenticated user ID.
     * @param {Object} filter - Fields to match on (e.g. { movie: "abc123" })
     * @param {Object} data - Fields to create/update
     * @param {string} [targetUser] - Optional: write as another pod member (server verifies membership)
     * @returns {Object} Created or updated record
     */
    async upsert(filter, data, targetUser) {
        const headers = await this._authHeaders(true);

        const body = { filter, data };
        if (targetUser) body.targetUser = targetUser;

        const res = await fetch(`${this._apiBase}/pb/${this._name}/upsert`, {
            method: 'POST',
            headers,
            body: JSON.stringify(body),
        });

        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw buildError(res, err, 'Upsert failed');
        }
        return res.json();
    }
}

export default class ProxyClient {
    constructor(getToken) {
        this._getToken = getToken;
        this._cache = {};
    }

    /**
     * Get a collection proxy (cached per collection name).
     * @param {string} name - PocketBase collection name
     * @returns {CollectionProxy}
     */
    collection(name) {
        if (!this._cache[name]) {
            this._cache[name] = new CollectionProxy(name, this._getToken);
        }
        return this._cache[name];
    }
}
